import type { ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { ServiceQueryResultWithFetch } from '@atlassian/jira-software-deployments-service-query-types/src/services/types.tsx';
import type { ChartData, DateRange, TopFilter } from '../../common/types';
import { getMaybeCombinedError } from '../../common/utils/errors';
import { useHistoricalCycleTimeData } from '../historical-cycle-time';
import { useIssueTableData } from '../issue-table';
import { usePerIssueMetrics } from '../per-issue-metrics';
import { transformData } from './utils';

export const useDateRangeIssueMetrics = (
	projectId: ProjectId | null,
	selectedDateRange: DateRange | null,
	filters: TopFilter,
): ServiceQueryResultWithFetch<ChartData> => {
	const perIssueMetricsResult = usePerIssueMetrics(projectId, selectedDateRange, filters);
	const historicalCycleTimeResult = useHistoricalCycleTimeData(projectId, filters);
	const issueDetailQueryResult = useIssueTableData(projectId, selectedDateRange, filters);
	const isLoading =
		perIssueMetricsResult.loading ||
		historicalCycleTimeResult.loading ||
		issueDetailQueryResult.loading;

	const resultBase = {
		loading: isLoading,
		error: getMaybeCombinedError([perIssueMetricsResult.error, historicalCycleTimeResult.error]),
		fetch: perIssueMetricsResult.fetch,
	};

	return {
		...resultBase,
		data:
			historicalCycleTimeResult.data == null ||
			perIssueMetricsResult.data == null ||
			issueDetailQueryResult.data == null ||
			selectedDateRange == null
				? undefined
				: transformData(
						perIssueMetricsResult.data,
						historicalCycleTimeResult.data,
						selectedDateRange,
						issueDetailQueryResult.data,
					),
	};
};
